import { useParams } from 'react-router-dom';

import styled from 'styled-components';
import { PlatformCode } from 'type/common';

import { useGetCreatorProfile } from 'hooks/queries/creator';
import CorePictureAvatar from 'shared/avatar/CorePictureAvatar';
import CoreLoading from 'shared/CoreLoading';
import CoreSvg from 'shared/CoreSvg';
import CoreTooltip from 'shared/CoreTooltip';
import Icon from 'shared/icons';
import {
	ReportSection,
	ReportSectionCard,
	ReportSectionHeader,
} from 'styles/styled-components/report';
import style from 'styles/styled-components/style';
import round from 'utils/round';

const BasicProfile = () => {
	const { platformCode, creatorPk, groupId } = useParams();

	const {
		data: creatorProfile,
		isLoading: is_loading,
		isError: is_error,
	} = useGetCreatorProfile(creatorPk);

	const data = creatorProfile;

	const platform_report_mapper = {
		ig: {
			url: () => data?.link ?? '',
			item_list: [
				{
					name: '게시물',
					content: () => Number(data?.mediaCount ?? 0),
					unit: '',
				},
				{
					name: '팔로워',
					content: () => Number(data?.followers ?? 0),
					unit: '명',
				},
				{
					name: '팔로잉',
					content: () => Number(data?.followings ?? 0),
					unit: '명',
				},
				{
					name: '참여 이력',
					content: () => Number(data?.participationCount ?? 0),
					unit: '회',
				},
			],
			icon: <Icon.Insta />,
			profile: () => data?.profileImage,
			username: () => data?.username,
			full_name: () => data?.fullname,
			desc: () => data?.biography,
		},
		yt: {
			url: () => data?.link,
			item_list: [
				{
					name: '총 영상',
					content: () => Number(data?.mediaCount),
					unit: '개',
				},
				{
					name: '구독자',
					content: () => Number(data?.followers),
					unit: '명',
				},
			],
			icon: <Icon.Youtube />,
			profile: () => data?.profileImage,
			username: () => data?.username,
			full_name: () => data?.fullname,
			desc: () => data?.biography,
		},
	};

	if (!data || is_loading || is_error || !data?.username)
		return (
			<ReportSection>
				<ReportSectionHeader>기본 프로필</ReportSectionHeader>
				<Text typo="p" _margin="0 0 16px 0">
					인플루언서 기본 정보입니다.
				</Text>

				<Grid flex="start" direction="column" _gap="16px">
					<Grid flex="between" _gap="16px" _height="120px">
						<ReportSectionCard animation={true} _width="70%">
							<Grid _height="100%" flex="center">
								<CoreLoading color_type="gray" />
							</Grid>
						</ReportSectionCard>

						<ReportSectionCard animation={true} _width="30%" delay="0.5s">
							<Grid _height="100%" flex="center">
								<CoreLoading color_type="gray" />
							</Grid>
						</ReportSectionCard>
					</Grid>
				</Grid>
			</ReportSection>
		);

	return (
		<ReportSection>
			<ReportSectionHeader>기본 프로필</ReportSectionHeader>
			<Text typo="p" _margin="0 0 16px 0">
				인플루언서 기본 정보입니다.
			</Text>

			<Grid flex="start" direction="column" _gap="16px">
				<Grid flex="between" _gap="16px" _height="120px">
					<ReportSectionCard _width="70%">
						<Grid flex="start" _gap="16px" _height="100%">
							<CorePictureAvatar
								size="default"
								color_type="yellow"
								text={
									platform_report_mapper[platformCode as PlatformCode].username() ??
									'influencer'
								}
								picture={platform_report_mapper[platformCode as PlatformCode].profile()}
							/>
							<Grid _width="100%">
								<Grid flex="start" _gap="8px" _overflow="hidden" _width="100%">
									<CoreSvg>
										{platform_report_mapper[platformCode as PlatformCode].icon}
									</CoreSvg>
									<Text typo="p_bold">
										{platform_report_mapper[platformCode as PlatformCode].username()}
									</Text>
									<TitleWrap typo="span">
										{platform_report_mapper[platformCode as PlatformCode].full_name()}
									</TitleWrap>
									<a
										href={platform_report_mapper[platformCode as PlatformCode].url()}
										target="_blank"
										rel="noreferrer"
									>
										<CoreSvg>
											<Icon.OutLink />
										</CoreSvg>
									</a>
								</Grid>

								<Grid _overflow="hidden" _width="600px" flex="start">
									<DesciptionWrap typo="p">
										{platform_report_mapper[platformCode as PlatformCode].desc()}
									</DesciptionWrap>

									<CoreTooltip
										direction="down"
										text={platform_report_mapper[platformCode as PlatformCode].desc()}
										_width="auto"
										_disabled={
											platform_report_mapper[platformCode as PlatformCode].desc() === ''
										}
									>
										<Text typo="p">더보기</Text>
									</CoreTooltip>
								</Grid>
							</Grid>
						</Grid>
					</ReportSectionCard>

					<ReportSectionCard _width="30%">
						<Grid flex="between" _height="100%" _gap="16px">
							<Grid align="center">
								<Text typo="span" _margin="0 0 16px 0">
									{platform_report_mapper[platformCode as PlatformCode].item_list[0].name}
								</Text>
								<Text typo="p_bold">
									{round(
										Number(
											platform_report_mapper[
												platformCode as PlatformCode
											].item_list[0].content(),
										),
										'round',
									) +
										` ${
											platform_report_mapper[platformCode as PlatformCode].item_list[0]
												.unit
										}` ||
										'' +
											` ${
												platform_report_mapper[platformCode as PlatformCode].item_list[0]
													.unit
											}`}
								</Text>
							</Grid>
							<Grid align="center">
								<Text typo="span" _margin="0 0 16px 0">
									{platform_report_mapper[platformCode as PlatformCode].item_list[1].name}
								</Text>
								<Text typo="p_bold">
									{round(
										Number(
											platform_report_mapper[
												platformCode as PlatformCode
											].item_list[1].content(),
										),
										'round',
									) +
										` ${
											platform_report_mapper[platformCode as PlatformCode].item_list[1]
												.unit
										}` ||
										'' +
											` ${
												platform_report_mapper[platformCode as PlatformCode].item_list[1]
													.unit
											}`}
								</Text>
							</Grid>
							<Grid align="center">
								<Text typo="span" _margin="0 0 16px 0">
									{platform_report_mapper[platformCode as PlatformCode].item_list[2].name}
								</Text>
								<Text typo="p_bold">
									{round(
										Number(
											platform_report_mapper[
												platformCode as PlatformCode
											].item_list[2].content(),
										),
										'round',
									) +
										` ${
											platform_report_mapper[platformCode as PlatformCode].item_list[2]
												.unit
										}` ||
										'' +
											` ${
												platform_report_mapper[platformCode as PlatformCode].item_list[2]
													.unit
											}`}
								</Text>
							</Grid>
							<Grid align="center">
								<Text typo="span" _margin="0 0 16px 0">
									{platform_report_mapper[platformCode as PlatformCode].item_list[2].name}
								</Text>
								<Text typo="p_bold">
									{round(
										Number(
											platform_report_mapper[
												platformCode as PlatformCode
											].item_list[3].content(),
										),
										'round',
									) +
										` ${
											platform_report_mapper[platformCode as PlatformCode].item_list[3]
												.unit
										}` ||
										'' +
											` ${
												platform_report_mapper[platformCode as PlatformCode].item_list[3]
													.unit
											}`}
								</Text>
							</Grid>
						</Grid>
					</ReportSectionCard>
				</Grid>
			</Grid>
		</ReportSection>
	);
};

const { Grid, Text } = style;

const TitleWrap = styled(Text)`
	${({ theme }) => theme.common.ellipsis};
`;

const DesciptionWrap = styled(Text)`
	${({ theme }) => theme.common.ellipsis};
	margin-right: 8px;
`;

const RankingWrap = styled(Grid)`
	padding: 8px 16px;
	border-radius: 8px;
	background-color: ${({ theme }) => theme.colors.uplus.bg10_lighten};
`;

const ReportGrid = styled(Grid)`
	display: grid;
	grid-template-columns: 246px 246px 1fr;
	grid-gap: 16px;
`;

const TagArrow = styled.div`
	width: 8px;
	height: 21px;

	${({ theme }) => theme.typo.body.span_bold};
	color: ${({ theme }) => theme.colors.gray.gray15};
`;

export default BasicProfile;
